import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getSubscriptionStatus, createCheckoutSession, SubscriptionStatus, SubscriptionTier } from '../services/api';

export function useSubscription() {
  const { userId, user, getToken } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const userEmail = user?.primaryEmailAddress?.emailAddress;

  const loadSubscription = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    try {
      const status = await getSubscriptionStatus(userId, userEmail, getToken);
      setSubscription(status);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscription');
      console.error('Error loading subscription:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, userEmail, getToken]);

  useEffect(() => {
    if (userId) {
      loadSubscription();
    } else {
      setLoading(false);
    }
  }, [userId, loadSubscription]);

  const upgrade = async (tier: SubscriptionTier) => {
    if (!userId) {
      throw new Error('You must be signed in to upgrade');
    }

    const successUrl = `${window.location.origin}?upgrade=success`;
    const cancelUrl = `${window.location.origin}?upgrade=cancelled`;

    try {
      const session = await createCheckoutSession(tier, successUrl, cancelUrl, userId, userEmail, getToken);
      // Redirect to Stripe checkout
      window.location.href = session.url;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create checkout session';
      setError(errorMessage);
      throw err;
    }
  };

  return {
    subscription,
    loading,
    error,
    isPremium: subscription?.is_premium ?? false,
    tier: subscription?.tier ?? 'free',
    upgrade,
    refresh: loadSubscription,
    // Expose subscription limits for feature enforcement
    maxPriceAlerts: subscription?.max_price_alerts ?? 5,
    maxSavedSearches: subscription?.max_saved_searches ?? 10,
    canExport: subscription?.can_export ?? false,
    adFree: subscription?.ad_free ?? false,
  };
}

